import { IconButton } from '@brightdrop/bd-ui';
import { useTranslations } from '@brightdrop/localization-client';
import { cx } from '@emotion/css';
import { usePermissions, useRole } from '@gm-commercial/profile-context';
import {
  AlertPermission,
  ProfilePermission,
  Role,
} from '@gm-commercial/profile-model';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';

import { SecureWrapper } from '~/common/components';
import { NOTIFICATIONS_REQUIRED_ROLES } from '~/common/models/notification.model';
import { ACCOUNT_PROFILE_PERSONAL_PATH } from '~/common/models/pages/accountPages.model';
import { SETTINGS_NOTIFICATIONS_VEHICLES } from '~/common/models/pages/settingsPages.model';
import { SUPPORT_CONTACT_PATH } from '~/common/models/pages/supportPages.model';
import { AppRoutePaths } from '~/common/models/route.model';
import {
  getNavDisabledPaths,
  getProfileDisabled,
} from '~/common/utils/route/route.utils';

import {
  selectActingPermissionsContextIds,
  selectActingProfileFirstName,
  selectActingProfileId,
  selectActingProfileLastName,
  selectActingProfileOrganization,
  selectActingProfileStatus,
  selectActingRole,
} from '~/store/profile/profileSlice.selectors';
import GlobalSearch from './components/GlobalSearch/GlobalSearch';
import NotificationBadge from './components/NotificationBadge/NotificationBadge';
import ProfileMenu from './components/ProfileMenu/ProfileMenu';
import useStyles from './GlobalHeader.styles';

const MESSAGES = {
  'common:header.openUserMenu': 'Open user menu',
  'common:header.profileMenu': 'Profile menu',
  'common:header.logout': 'Logout',
  'account:support.nav.title': 'Support',
  'common:account': 'Account',
  'common:header.settings': 'Settings',
  roleList: {
    [`common:roleList.${Role.ORG_MANAGER}`]: 'Account administrator',
    [`common:roleList.${Role.HUB_MANAGER}`]: 'Hub Manager',
    [`common:roleList.${Role.FLEET_MANAGER}`]: 'Fleet Manager',
    [`common:roleList.${Role.OPERATOR}`]: 'Operator',
    [`common:roleList.${Role.SYSTEM_MANAGER}`]: 'Advisor',
    [`common:roleList.${Role.SYSTEM_READER}`]: 'System Reader',
    [`common:roleList.${Role.VIEWER}`]: 'Viewer',
    [`common:roleList.${Role.TECHNICAL_MANAGER}`]: 'Technical Manager',
  },
};

const selectActingProfileParams = createSelector(
  selectActingProfileId,
  selectActingProfileFirstName,
  selectActingProfileLastName,
  selectActingRole,
  selectActingProfileStatus,
  selectActingProfileOrganization,
  selectActingPermissionsContextIds,
  (usersId, firstName, lastName, role, status, organization, contextIds) => {
    return {
      usersId,
      firstName,
      lastName,
      role,
      status,
      organization,
      ...contextIds,
    };
  }
);

const Header = (): JSX.Element => {
  const { classes } = useStyles();
  const { translations } = useTranslations(MESSAGES);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const menuId = 'account-menu';
  const { hasPermission } = usePermissions();
  const { hasRole: hasSignedInRole } = useRole(false);
  const actingProfileParams = useSelector(selectActingProfileParams);
  const location = useLocation();
  const matchUnsupportedRoute = getNavDisabledPaths().some((path) =>
    matchPath(path, location.pathname)
  );
  const matchSessionRoute = matchPath(
    AppRoutePaths.SERVICE_UNAVAILABLE,
    location.pathname
  );
  const unSpportedRoute = getProfileDisabled().some((path) =>
    matchPath(path, location.pathname)
  );
  const disableProfile = !!unSpportedRoute;
  const suppressNavigation =
    !!matchUnsupportedRoute || !actingProfileParams.usersId;

  const canUpdateProfile = useMemo(
    () => hasPermission([ProfilePermission.UPDATE]),
    [hasPermission]
  );
  const PROFILE_MENU = useMemo(
    () => [
      {
        label: 'common:header.logout',
        testId: 'logout-menu',
        onClick: { redirect: AppRoutePaths.LOGOUT },
        section: 'bottom',
      },
    ],
    []
  );
  const updatedProfileMenu = useMemo(() => {
    const { role, usersId } = actingProfileParams;
    const isNotificationAllowed = hasSignedInRole(NOTIFICATIONS_REQUIRED_ROLES);
    const updatedProfileMenu =
      usersId &&
      role !== Role.OPERATOR &&
      !suppressNavigation &&
      !disableProfile &&
      canUpdateProfile
        ? [
            ...[
              {
                label: 'common:account',
                testId: 'account-menu',
                onClick: {
                  redirect: ACCOUNT_PROFILE_PERSONAL_PATH,
                },
                section: 'top',
              },
            ],
            ...(isNotificationAllowed
              ? [
                  {
                    label: 'common:header.settings',
                    testId: 'settings-menu',
                    onClick: {
                      redirect: SETTINGS_NOTIFICATIONS_VEHICLES,
                    },
                    section: 'top',
                  },
                ]
              : []),
            {
              label: 'account:support.nav.title',
              testId: 'support-menu',
              onClick: {
                redirect: SUPPORT_CONTACT_PATH, // use SUPPORT_FAQS_PATH when FAQs feature flag(SDPCPLT-63-FAQS-PAGE) is enabled in prod
              },
              section: 'top',
            },
            ...[],
            ...PROFILE_MENU,
          ]
        : PROFILE_MENU;

    return updatedProfileMenu;
  }, [
    actingProfileParams,
    hasSignedInRole,
    suppressNavigation,
    disableProfile,
    canUpdateProfile,
    PROFILE_MENU,
  ]);

  const handleMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const theme = useTheme();
  const isDesktopSmallViewport = useMediaQuery(theme.breakpoints.down('lg'));

  return (
    <AppBar
      className={cx(classes.headerWrapper, {
        hideBar: suppressNavigation,
      })}
      position={isDesktopSmallViewport ? 'sticky' : 'relative'}
      color="inherit"
    >
      <Box className={classes.container}>
        <Toolbar
          disableGutters
          className={classes.header}
          sx={{ padding: 0, minHeight: 0 }}
        >
          <SecureWrapper requiredPermissions={[]} passThrough>
            <GlobalSearch />
          </SecureWrapper>
          <SecureWrapper
            requiredPermissions={[AlertPermission.READ]}
            requiredRoles={NOTIFICATIONS_REQUIRED_ROLES}
            actingRole={false}
          >
            <NotificationBadge />
          </SecureWrapper>
          <SecureWrapper
            requiredPermissions={[]}
            passThrough={!!matchSessionRoute}
          >
            <div>
              <IconButton
                id="user-menu-btn"
                aria-label={translations['common:header.openUserMenu']}
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleMenu}
                /* Override necessary as it follows a different design pattern than other icon buttons. */
                className={classes.profileContainerButton}
                icon={
                  <div
                    className={cx(classes.profileContainer, {
                      isOpen: !!anchorEl,
                    })}
                    data-app-version={globalThis?.appConfig?.version}
                  >
                    {actingProfileParams?.firstName?.charAt(0)}
                    {actingProfileParams?.lastName?.charAt(0)}
                  </div>
                }
              />
              <ProfileMenu
                label={translations['common:header.profileMenu']}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                items={updatedProfileMenu.map(
                  ({ label, testId, onClick, section }) => ({
                    label:
                      translations[
                        label as keyof Omit<typeof translations, 'roleList'>
                      ],
                    testId,
                    onClick: { redirect: onClick?.redirect },
                    section: section,
                  })
                )}
              />
            </div>
          </SecureWrapper>
        </Toolbar>
      </Box>
    </AppBar>
  );
};

export default Header;
